import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Tab, Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import EmailSettings from '../components/EmailSettings';
import SavedEmails from '../components/SavedEmails';
import SiteSettings from '../components/SiteSettings';
import EmailTemplates from '../components/EmailTemplates';

const AdminDashboard = () => {
  const { user, updateUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Silme modalı için state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Düzenleme modalı için state
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    isAdmin: false
  });

  // Kullanıcıları getir
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const { data } = await axios.get('/api/users', config);
      
      if (data.success) {
        setUsers(data.users);
      }
    } catch (error) {
      setError('Kullanıcılar yüklenirken bir hata oluştu');
      toast.error('Kullanıcılar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();

    // Kullanıcı listesi güncelleme eventini dinle
    const handleUsersUpdated = (event) => {
      setUsers(event.detail);
    };

    window.addEventListener('usersUpdated', handleUsersUpdated);

    return () => {
      window.removeEventListener('usersUpdated', handleUsersUpdated);
    };
  }, []);
  
  // Sayfa yüklendiğinde filtre alanına focus ol
  useEffect(() => {
    if (!loading && !error) {
      setTimeout(() => {
        filterInputRef.current?.focus();
      }, 100);
    }
  }, [loading, error]);

  // Silme modalını aç
  const handleShowDeleteModal = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // Silme modalını kapat
  const handleCloseDeleteModal = () => {
    setUserToDelete(null);
    setShowDeleteModal(false);
  };

  // Kullanıcıyı sil
  const handleDeleteUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const { data } = await axios.delete(`/api/users/${userToDelete._id}`, config);
      
      if (data.success) {
        toast.success('Kullanıcı başarıyla silindi');
        setUsers(users.filter(u => u._id !== userToDelete._id));
        handleCloseDeleteModal();
        
        // Kullanıcı silindikten sonra filtre alanına focus ol
        // Toast bildirimi gösterildikten sonra focus olmak için süreyi artır
        setTimeout(() => {
          filterInputRef.current?.focus();
        }, 200);
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Kullanıcı silinirken bir hata oluştu';
      toast.error(message);
    }
  };

  // Düzenleme modalını aç
  const handleShowEditModal = (user) => {
    setUserToEdit(user);
    setEditFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      isAdmin: user.isAdmin
    });
    setShowEditModal(true);
  };
  
  // Modal açıldığında ilk alanı seç
  useEffect(() => {
    if (showEditModal) {
      setTimeout(() => {
        const firstInput = document.querySelector('.modal-body input[type="text"]');
        if (firstInput) {
          firstInput.select();
        }
      }, 100);
    }
  }, [showEditModal]);

  // Düzenleme modalını kapat
  const handleCloseEditModal = () => {
    setUserToEdit(null);
    setShowEditModal(false);
    
    // Modal kapandığında filtre alanına focus ol
    // Toast bildirimi gösterildikten sonra focus olmak için süreyi artır
    setTimeout(() => {
      filterInputRef.current?.focus();
    }, 200);
  };

  // Form değişikliklerini takip et
  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Kullanıcıyı güncelle
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const { data } = await axios.put(`/api/users/${userToEdit._id}`, editFormData, config);
      
      if (data.success) {
        toast.success('Kullanıcı başarıyla güncellendi');
        setUsers(users.map(u => u._id === userToEdit._id ? data.user : u));
        
        // Eğer güncellenen kullanıcı mevcut kullanıcı ise AuthContext'i güncelle
        if (userToEdit._id === user._id) {
          updateUser(data.user);
        }
        
        handleCloseEditModal();
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Kullanıcı güncellenirken bir hata oluştu';
      toast.error(message);
    }
  };

  // Pagination için state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Filtreleme için state
  const [filterText, setFilterText] = useState('');
  const filterInputRef = useRef(null);
  
  // Aktif sekme için state
  const [activeTab, setActiveTab] = useState('users');
  
  // Sekme değiştiğinde izle
  useEffect(() => {
    // Kullanıcılar sekmesine geçildiğinde filtre alanına focus ol
    if (activeTab === 'users' && !loading && !error) {
      setTimeout(() => {
        filterInputRef.current?.focus();
      }, 200);
    }
    
    // E-mail Adresleri sekmesine geçildiğinde filtre alanına focus ol
    if (activeTab === 'savedEmails') {
      // SavedEmails bileşeni yüklendiğinde otomatik olarak focus olacak
      // Ancak toast alert'leri yüzünden sorun olmaması için biraz bekleyelim
      setTimeout(() => {
        // Doğrudan DOM'a erişerek SavedEmails bileşenindeki filtre alanına focus ol
        const savedEmailsFilterInput = document.querySelector('.tab-pane.active input[type="text"]');
        if (savedEmailsFilterInput) {
          savedEmailsFilterInput.focus();
        }
      }, 200);
    }
  }, [activeTab, loading, error]);
  
  // Sıralama için state
  const [sortField, setSortField] = useState('firstName');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Filtrelenmiş kullanıcılar
  const filteredUsers = users.filter(user => {
    const searchText = filterText.toLowerCase();
    return (
      user.firstName.toLowerCase().includes(searchText) ||
      user.lastName.toLowerCase().includes(searchText) ||
      user.email.toLowerCase().includes(searchText)
    );
  });
  
  // Sıralanmış kullanıcılar
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // Boolean değerler için özel işlem
    if (typeof aValue === 'boolean') {
      return sortDirection === 'asc' 
        ? (aValue === bValue ? 0 : aValue ? 1 : -1)
        : (aValue === bValue ? 0 : aValue ? -1 : 1);
    }
    
    // String değerler için
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  
  // Pagination için hesaplamalar
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = sortedUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  
  // Sayfa değiştirme
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // Sayfa başına gösterilecek öğe sayısını değiştirme
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Sayfa sayısı değiştiğinde ilk sayfaya dön
  };

  return (
    <div>
      <Tabs 
        defaultActiveKey="users" 
        className="mb-3"
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key)}
      >
        <Tab eventKey="users" title="Kullanıcılar">
          {loading ? (
            <div className="text-center my-3">Yükleniyor...</div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Form.Group className="d-flex align-items-center" style={{ width: '300px' }}>
                  <Form.Control
                    ref={filterInputRef}
                    type="text"
                    placeholder="Ad, soyad veya e-posta ile ara..."
                    value={filterText}
                    onChange={(e) => {
                      setFilterText(e.target.value);
                      setCurrentPage(1); // Filtreleme yapıldığında ilk sayfaya dön
                    }}
                    autoComplete="off"
                  />
                  {filterText && (
                    <Button 
                      variant="outline-secondary" 
                      className="ms-2"
                      onClick={() => {
                        setFilterText('');
                        setCurrentPage(1);
                        // Temizle butonuna tıklayınca filtre alanına focus ol
                        setTimeout(() => {
                          filterInputRef.current?.focus();
                        }, 0);
                      }}
                    >
                      Temizle
                    </Button>
                  )}
                </Form.Group>
                
                <Form.Group className="d-flex align-items-center">
                  <Form.Label className="me-2 mb-0">Sayfa başına:</Form.Label>
                  <Form.Select 
                    value={itemsPerPage} 
                    onChange={handleItemsPerPageChange}
                    style={{ width: '80px' }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </Form.Select>
                </Form.Group>
              </div>
              
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th 
                      onClick={() => {
                        if (sortField === 'firstName') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('firstName');
                          setSortDirection('asc');
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                      className="user-select-none"
                    >
                      Ad {sortField === 'firstName' && (
                        <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                    <th 
                      onClick={() => {
                        if (sortField === 'lastName') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('lastName');
                          setSortDirection('asc');
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                      className="user-select-none"
                    >
                      Soyad {sortField === 'lastName' && (
                        <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                    <th 
                      onClick={() => {
                        if (sortField === 'email') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('email');
                          setSortDirection('asc');
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                      className="user-select-none"
                    >
                      E-posta {sortField === 'email' && (
                        <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                    <th 
                      onClick={() => {
                        if (sortField === 'isAdmin') {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortField('isAdmin');
                          setSortDirection('asc');
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                      className="user-select-none"
                    >
                      Admin {sortField === 'isAdmin' && (
                        <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {currentUsers.map(user => (
                    <tr key={user._id}>
                      <td>{user.firstName}</td>
                      <td>{user.lastName}</td>
                      <td>{user.email}</td>
                      <td>{user.isAdmin ? 'Evet' : 'Hayır'}</td>
                      <td>
                        <Button 
                          variant="primary" 
                          size="sm" 
                          className="me-2"
                          onClick={() => handleShowEditModal(user)}
                        >
                          Düzenle
                        </Button>
                        <Button 
                          variant="danger" 
                          size="sm"
                          onClick={() => handleShowDeleteModal(user)}
                          disabled={user.email === 'onderxyilmaz@gmail.com'}
                        >
                          Sil
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {/* Pagination */}
              {users.length > 0 && (
                <div className="d-flex justify-content-center mt-3">
                  <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Önceki
                      </button>
                    </li>
                    
                    {[...Array(totalPages).keys()].map(number => (
                      <li 
                        key={number + 1} 
                        className={`page-item ${currentPage === number + 1 ? 'active' : ''}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(number + 1)}
                        >
                          {number + 1}
                        </button>
                      </li>
                    ))}
                    
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Sonraki
                      </button>
                    </li>
                  </ul>
                </div>
              )}
              
              <div className="text-center mt-2">
                <small className="text-muted">
                  Toplam {users.length} kullanıcı, {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, users.length)} arası gösteriliyor
                </small>
              </div>
            </>
          )}
        </Tab>
        <Tab eventKey="email" title="E-mail Ayarları">
          <EmailSettings />
        </Tab>
        <Tab eventKey="savedEmails" title="E-mail Adresleri">
          <SavedEmails />
        </Tab>
        <Tab eventKey="siteSettings" title="Site Yönetimi">
          <SiteSettings />
        </Tab>
        <Tab eventKey="email-templates" title="E-mail Template">
          <EmailTemplates active={activeTab === 'email-templates'} />
        </Tab>
      </Tabs>
      
      {/* Silme Onay Modalı */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>Kullanıcıyı Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{userToDelete?.firstName} {userToDelete?.lastName}</strong> adlı kullanıcıyı silmek istediğinize emin misiniz?
          </p>
          <p className="text-danger">Bu işlem geri alınamaz!</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDeleteUser}>
            Sil
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Düzenleme Modalı */}
      <Modal show={showEditModal} onHide={handleCloseEditModal}>
        <Modal.Header closeButton>
          <Modal.Title>Kullanıcıyı Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdateUser}>
            <Form.Group className="mb-3">
              <Form.Label>Ad</Form.Label>
              <Form.Control
                type="text"
                name="firstName"
                value={editFormData.firstName}
                onChange={handleEditFormChange}
                required
                autoFocus
                autoComplete="off"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Soyad</Form.Label>
              <Form.Control
                type="text"
                name="lastName"
                value={editFormData.lastName}
                onChange={handleEditFormChange}
                required
                autoComplete="off"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>E-posta</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={editFormData.email}
                onChange={handleEditFormChange}
                required
                autoComplete="off"
                readOnly
                disabled
              />
              <Form.Text className="text-muted">
                E-posta adresi değiştirilemez.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="isAdmin"
                label="Admin Yetkisi"
                checked={editFormData.isAdmin}
                onChange={handleEditFormChange}
                disabled={userToEdit?.email === 'onderxyilmaz@gmail.com'}
              />
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={handleCloseEditModal}>
                İptal
              </Button>
              <Button variant="primary" type="submit">
                Kaydet
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminDashboard;
